import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";


import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EventVenuesCard } from "@/components/event-venues-card";

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

export default function EventEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [eventType, setEventType] = useState("match_day");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [schoolName, setSchoolName] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getEvent(id)
      .then((res) => {
        const e = res.event;
        setName(e.name);
        setEventType(e.event_type);
        setStartsAt(toDatetimeLocal(e.starts_at));
        setEndsAt(toDatetimeLocal(e.ends_at));
        setSchoolId(e.school_id);
        setSchoolName(e.school_name);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load event",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);

    try {
      await api.updateEvent(id, {
        name,
        event_type: eventType as "match_day" | "practice" | "tryout",
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      });
      navigate(`/events/${id}`);
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to update event",
      );
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="event-edit" data-state="loading">Loading...</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="event-edit" data-state={saving ? "submitting" : error ? "error" : "ready"}>
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to={`/schools/${schoolId}`} className="hover:underline">
            {schoolName || "School"}
          </Link>
          <span>/</span>
          <Link
            to={`/schools/${schoolId}/events`}
            className="hover:underline"
          >
            Events
          </Link>
          <span>/</span>
          <Link to={`/events/${id}`} className="hover:underline">
            {name}
          </Link>
          <span>/</span>
          <span>Edit</span>
        </div>
        <h1 className="text-2xl font-semibold">Edit Event</h1>
      </div>

      <form onSubmit={handleSubmit} className="flex max-w-md flex-col gap-4" data-region="form" data-form="event-edit">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <div className="flex flex-col gap-2">
          <Label htmlFor="name">Event Name</Label>
          <Input
            id="name"
            data-field="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="event_type">Event Type</Label>
          <select
            id="event_type"
            data-field="event-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            required
          >
            <option value="match_day">Match Day</option>
            <option value="practice">Practice</option>
            <option value="tryout">Tryout</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="starts_at">Start Time</Label>
          <Input
            id="starts_at"
            data-field="starts-at"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="ends_at">End Time</Label>
          <Input
            id="ends_at"
            data-field="ends-at"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex gap-3">
          <Button type="submit" data-action="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-action="cancel"
            onClick={() => navigate(`/events/${id}`)}
          >
            Cancel
          </Button>
        </div>
      </form>

      {/* 100 -- Venues card */}
      {id && <EventVenuesCard eventId={id} eventSchoolId={schoolId} />}
    </div>
  );
}
